import { z } from "zod";
import { TagUidSchema } from "./timeclock";

/**
 * Shared wire schemas & types for WebAuthn / Security Keys (SPEC-43).
 */

export const WebauthnRegisterBeginResponse = z.object({
  options: z.record(z.unknown()), // PublicKeyCredentialCreationOptionsJSON
  challengeToken: z.string(),
});
export type WebauthnRegisterBeginResponse = z.infer<typeof WebauthnRegisterBeginResponse>;

export const WebauthnRegisterFinishRequest = z.object({
  challengeToken: z.string(),
  response: z.record(z.unknown()), // RegistrationResponseJSON
  label: z.string().trim().max(100).optional(),
});
export type WebauthnRegisterFinishRequest = z.infer<typeof WebauthnRegisterFinishRequest>;

export const WebauthnCredentialInfo = z.object({
  id: z.string(),
  label: z.string().nullable(),
  deviceType: z.string().nullable(),
  backedUp: z.boolean(),
  registeredAt: z.string(),
  lastUsedAt: z.string().nullable(),
});
export type WebauthnCredentialInfo = z.infer<typeof WebauthnCredentialInfo>;

// SPEC-68 §3.4(a) — driver identifier-first passkey login.
export const PasskeyLoginBeginRequest = z.object({
  userId: z.string().uuid(),
  tagUid: TagUidSchema.optional(), // SPEC-70 — gate sits after the creds guard, before the challenge mint (§2.5's begin sample is normative; audit #1 fix A)
});
export type PasskeyLoginBeginRequest = z.infer<typeof PasskeyLoginBeginRequest>;

export const PasskeyLoginBeginResponse = z
  .object({
    available: z.boolean(),
    challengeToken: z.string().optional(),
    options: z.record(z.unknown()).optional(),
  })
  .refine(
    (d) => !d.available || (d.challengeToken !== undefined && d.options !== undefined),
    { message: "available=true requires challengeToken and options" },
  );
export type PasskeyLoginBeginResponse = z.infer<typeof PasskeyLoginBeginResponse>;

// SPEC-68 §3.2 — enrollment eligibility surfaced to the client. Declared
// BEFORE WebauthnCredentialsListResponse, which references it at module load.
export const EnrollmentStatus = z.object({
  canEnroll: z.boolean(),
  reason: z.string().nullable(),
});
export type EnrollmentStatus = z.infer<typeof EnrollmentStatus>;

export const WebauthnCredentialsListResponse = z.object({
  credentials: z.array(WebauthnCredentialInfo),
  officeKeyRequired: z.boolean(),
  // SPEC-68 §4.6 — passkey policy + the caller's enrollment eligibility.
  // `enrollment` is null for OFFICE (no presence gate applies to that role).
  passkeysRequired: z.boolean(),
  enrollment: EnrollmentStatus.nullable(),
});
export type WebauthnCredentialsListResponse = z.infer<typeof WebauthnCredentialsListResponse>;

export const WebauthnLoginFinishRequest = z.object({
  challengeToken: z.string(),
  response: z.record(z.unknown()), // AuthenticationResponseJSON
});
export type WebauthnLoginFinishRequest = z.infer<typeof WebauthnLoginFinishRequest>;
